import React, { useEffect, useState, useRef } from 'react';
import axios from '../utils/axiosInstance';
import { Button } from '@/components/ui/button';
import { PageLoader } from '@/components/ui/page-loader';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { FolderTree } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { BASE_URL } from '@/config/constants';
import { toast } from 'sonner';
import type { DragEndEvent } from '@dnd-kit/core';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Input } from '@/components/ui/input';

interface MasterCategory {
    _id: string;
    name: string;
    is_active: boolean;
    order: number;
    type: 'showcase' | 'image';
    parentCategory: {
        _id: string;
        name: string;
    } | null;
}

interface SortableTableRowProps {
    category: MasterCategory;
    handleEdit: (category: MasterCategory) => void;
    setDeleteId: (id: string) => void;
    categories: MasterCategory[];
    setCategories: (categories: MasterCategory[]) => void;
}

const SortableTableRow: React.FC<SortableTableRowProps> = ({
    category,
    handleEdit,
    setDeleteId,
    categories,
    setCategories,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: category._id });

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        backgroundColor: isDragging ? 'rgba(0, 0, 0, 0.05)' : undefined,
    };

    return (
        <TableRow ref={setNodeRef} style={style} className="hover:bg-muted/50 transition-colors">
            <TableCell className="w-10 select-none cursor-grab" {...attributes} {...listeners}>
                <div className="flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-muted-foreground">
                        <circle cx="9" cy="12" r="1"/><circle cx="9" cy="5" r="1"/><circle cx="9" cy="19" r="1"/>
                        <circle cx="15" cy="12" r="1"/><circle cx="15" cy="5" r="1"/><circle cx="15" cy="19" r="1"/>
                    </svg>
                    <span className="w-4 text-muted-foreground font-medium">{category.order + 1}</span>
                </div>
            </TableCell>
            <TableCell className="font-medium text-foreground">{category.name}</TableCell>
            <TableCell>
              {category.parentCategory ? (
                <span className="badge-pill badge-info">
                  {category.parentCategory.name}
                </span>
              ) : (
                <span className="badge-pill badge-neutral">
                  Top Level
                </span>
              )}
            </TableCell>
            <TableCell>
                <div className="flex items-center space-x-2">
                    <Switch
                        checked={category.is_active}
                        onCheckedChange={() => {
                            axios.put(BASE_URL + `/api/master-category/${category._id}`, { is_active: !category.is_active })
                                .then(() => {
                                    toast.success(`Master category ${!category.is_active ? 'activated' : 'deactivated'} successfully!`);
                                    const updatedCategories = categories.map(c => c._id === category._id ? { ...c, is_active: !c.is_active } : c);
                                    setCategories(updatedCategories);
                                })
                                .catch((err: any) => {
                                    toast.error(err.response?.data?.message || 'Failed to update status');
                                });
                        }}
                        aria-label="Toggle category active state"
                    />
                    <span className={category.is_active ? 'status-label-active' : 'status-label-inactive'}>
                        {category.is_active ? 'Active' : 'Inactive'}
                    </span>
                </div>
            </TableCell>
            <TableCell>
                <div className="flex gap-2">
                    <Button 
                        variant="outline" 
                        size="icon-sm" 
                        onClick={() => handleEdit(category)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                    </Button>
                    <Button 
                        variant="destructive" 
                        size="icon-sm" 
                        onClick={() => setDeleteId(category._id)}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                        </svg>
                    </Button>
                </div>
            </TableCell>
        </TableRow>
    );
};

const emptyCategory = {
    name: '',
    is_active: true,
    order: 0,
    type: 'showcase' as 'showcase' | 'image',
    parentCategory: null as { _id: string; name: string } | null,
};

const MasterCategoryManager: React.FC = () => {
    const [categories, setCategories] = useState<MasterCategory[]>([]);
    const [form, setForm] = useState(emptyCategory);
    const [loading, setLoading] = useState(true);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editId, setEditId] = useState<string | null>(null);
    const [deleteId, setDeleteId] = useState<string | null>(null);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState<'showcase' | 'image'>('showcase');
    const [parentDropdownOpen, setParentDropdownOpen] = useState(false);
    const [editParentDropdownOpen, setEditParentDropdownOpen] = useState(false);

    const parentDropdownRef = useRef<HTMLDivElement>(null);
    const editParentDropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (parentDropdownRef.current && !parentDropdownRef.current.contains(event.target as Node)) {
                setParentDropdownOpen(false);
            }
            if (editParentDropdownRef.current && !editParentDropdownRef.current.contains(event.target as Node)) {
                setEditParentDropdownOpen(false);
            }
        };

        if (parentDropdownOpen || editParentDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [parentDropdownOpen, editParentDropdownOpen]);

    const fetchCategories = () => {
        setLoading(true);
        axios
            .get(BASE_URL + '/api/master-category', { params: { type: activeTab } })
            .then((res) => setCategories(res.data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        fetchCategories();
    }, [activeTab]);

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'parentCategory') {
            const selected = categories.find((cat) => cat._id === value) || null;
            setForm((prev) => ({ ...prev, parentCategory: selected }));
        } else {
            setForm((prev) => ({ ...prev, [name]: value }));
        }
    };

    const renderCategoryInputs = () => (
        <div className="space-y-2">
            <div className="relative" ref={parentDropdownRef}>
                <button
                    type="button"
                    onClick={() => setParentDropdownOpen(!parentDropdownOpen)}
                    className="px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between"
                >
                    {form.parentCategory ? form.parentCategory.name : 'Select Parent Category (Optional)'}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`transition-transform ${parentDropdownOpen ? 'rotate-180' : ''}`}
                    >
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
                {parentDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-full max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => {
                                setForm(prev => ({ ...prev, parentCategory: null }));
                                setParentDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                !form.parentCategory
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-foreground hover:bg-muted/50'
                            }`}
                        >
                            Select Parent Category (Optional)
                        </button>
                        {categories
                            .filter((cat) => !cat.parentCategory && cat._id !== editId)
                            .map((cat) => (
                                <button
                                    key={cat._id}
                                    type="button"
                                    onClick={() => {
                                        setForm(prev => ({ ...prev, parentCategory: { _id: cat._id, name: cat.name } }));
                                        setParentDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                        form.parentCategory?._id === cat._id
                                            ? 'bg-primary/10 text-primary font-medium'
                                            : 'text-foreground hover:bg-muted/50'
                                    }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );

    const renderEditCategoryInputs = () => (
        <div className="space-y-2">
            <div className="relative" ref={editParentDropdownRef}>
                <button
                    type="button"
                    onClick={() => setEditParentDropdownOpen(!editParentDropdownOpen)}
                    className="px-3 py-2 rounded-md bg-secondary hover:bg-accent text-sm font-medium text-foreground transition-colors flex items-center gap-2 w-full justify-between"
                >
                    {form.parentCategory ? form.parentCategory.name : 'Select Parent Category (Optional)'}
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className={`transition-transform ${editParentDropdownOpen ? 'rotate-180' : ''}`}
                    >
                        <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                </button>
                {editParentDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 min-w-full max-h-48 overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            onClick={() => {
                                setForm(prev => ({ ...prev, parentCategory: null }));
                                setEditParentDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                !form.parentCategory
                                    ? 'bg-primary/10 text-primary font-medium'
                                    : 'text-foreground hover:bg-muted/50'
                            }`}
                        >
                            Select Parent Category (Optional)
                        </button>
                        {categories
                            .filter((cat) => !cat.parentCategory && cat._id !== editId)
                            .map((cat) => (
                                <button
                                    key={cat._id}
                                    type="button"
                                    onClick={() => {
                                        setForm(prev => ({ ...prev, parentCategory: { _id: cat._id, name: cat.name } }));
                                        setEditParentDropdownOpen(false);
                                    }}
                                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                        form.parentCategory?._id === cat._id
                                            ? 'bg-primary/10 text-primary font-medium'
                                            : 'text-foreground hover:bg-muted/50'
                                    }`}
                                >
                                    {cat.name}
                                </button>
                            ))}
                    </div>
                )}
            </div>
        </div>
    );

    const handleAdd = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        try {
            const response = await axios.post(BASE_URL + '/api/master-category', {
                name: form.name,
                is_active: form.is_active,
                order: categories.length,
                type: activeTab,
                parentCategory: form.parentCategory?._id || null,
            });
            setAddOpen(false);
            setForm(emptyCategory);
            toast.success('Master category added and synced to all domains!');
            if (response.data) {
                setCategories(prev => [...prev, response.data]);
            } else {
                fetchCategories();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to add master category';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item: MasterCategory) => {
        setEditId(item._id);
        setForm({
            name: item.name,
            is_active: item.is_active,
            order: item.order,
            type: item.type,
            parentCategory: item.parentCategory,
        });
        setEditOpen(true);
        setError('');
    };

    const handleEditSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSaving(true);
        const currentEditId = editId;
        try {
            const response = await axios.put(BASE_URL + `/api/master-category/${editId}`, {
                name: form.name,
                is_active: form.is_active,
                parentCategory: form.parentCategory?._id || null,
            });
            setEditOpen(false);
            setEditId(null);
            setForm(emptyCategory);
            toast.success('Master category updated and synced to all domains!');
            if (response.data) {
                setCategories(prev => prev.map(category =>
                    category._id === currentEditId ? { ...category, ...response.data } : category
                ));
            } else {
                fetchCategories();
            }
        } catch (err: any) {
            const errorMessage = err.response?.data?.message || 'Failed to update master category';
            setError(errorMessage);
            toast.error(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        const deletedCategory = categories.find(category => category._id === deleteId);
        // Optimistic removal
        setCategories(prev => prev.filter(category => category._id !== deleteId));
        setDeleteId(null);
        try {
            await axios.delete(BASE_URL + `/api/master-category/${deleteId}`);
            toast.success('Master category and all associated user categories deleted!');
        } catch (err: any) {
            // Revert on error
            if (deletedCategory) {
                setCategories(prev => [...prev, deletedCategory].sort((a, b) => a.order - b.order));
            }
            const errorMessage = err.response?.data?.message || 'Failed to delete master category';
            toast.error(errorMessage);
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over) return;

        if (active.id !== over.id) {
            const oldIndex = categories.findIndex((item) => item._id === active.id);
            const newIndex = categories.findIndex((item) => item._id === over.id);

            try {
                await axios.put(
                    `${BASE_URL}/api/master-category/${active.id}/reorder`,
                    { newOrder: newIndex }
                );
                setCategories((items) => arrayMove(items, oldIndex, newIndex));
                toast.success('Master category reordered successfully!');
            } catch (err: any) {
                setCategories((items) => arrayMove(items, newIndex, oldIndex)); // Revert the order if API call fails
                toast.error(err.response?.data?.message || 'Failed to reorder master category');
            }
        }
    };

    const renderCategoryTable = () => (
        <>
            {loading ? (
                <PageLoader label="Loading master categories..." />
            ) : categories.length === 0 ? (
                <div className="empty-state">
                    <div className="empty-state-icon">
                        <FolderTree className="w-full h-full" />
                    </div>
                    <h3 className="empty-state-title">No master categories found</h3>
                    <p className="empty-state-description">Create your first master category to sync across all domains</p>
                </div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-muted/50">
                                <TableHead>Order</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Parent Category</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody className="text-left">
                            <SortableContext items={categories.map(c => c._id)} strategy={verticalListSortingStrategy}>
                                {categories.map((category) => (
                                    <SortableTableRow
                                        key={category._id}
                                        category={category}
                                        handleEdit={handleEdit}
                                        setDeleteId={setDeleteId}
                                        categories={categories}
                                        setCategories={setCategories}
                                    />
                                ))}
                            </SortableContext>
                        </TableBody>
                    </Table>
                </DndContext>
            )}
        </>
    );

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
                <div>
                    <h2 className="page-title">Master Category Management</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Categories created here will be automatically synced to all user domains
                    </p>
                </div>
                <div className="flex gap-2">
                    <Dialog open={addOpen} onOpenChange={setAddOpen}>
                        <DialogTrigger asChild>
                            <Button>Add {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Master Category</Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle>Add New {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} Master Category</DialogTitle>
                                <DialogDescription>Create a new master category that will be synced to all domains</DialogDescription>
                            </DialogHeader>
                            <form onSubmit={handleAdd} className="space-y-5">
                                <Input
                                    name="name"
                                    placeholder="Category Name"
                                    value={form.name}
                                    onChange={handleFormChange}
                                    required
                                />
                                {renderCategoryInputs()}
                                <div className="flex items-center space-x-3">
                                    <Switch
                                        checked={form.is_active}
                                        onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                                    />
                                    <Label>{form.is_active ? 'Active' : 'Inactive'}</Label>
                                </div>
                                {error && <div className="text-danger">{error}</div>}
                                <Button type="submit" loading={saving} loadingText="Adding...">Add</Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as 'showcase' | 'image')}>
                <TabsList>
                    <TabsTrigger value="showcase">Showcase Categories</TabsTrigger>
                    <TabsTrigger value="image">Image Gallery Categories</TabsTrigger>
                </TabsList>

                <TabsContent value="showcase" className="mt-4">
                    <div>{renderCategoryTable()}</div>
                </TabsContent>

                <TabsContent value="image" className="mt-4">
                    <div>{renderCategoryTable()}</div>
                </TabsContent>
            </Tabs>

            {/* Edit Dialog */}
            <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Master Category</DialogTitle>
                        <DialogDescription>Update the master category. Changes will sync to all domains.</DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEditSubmit} className="space-y-5">
                        <Input
                            name="name"
                            placeholder="Category Name"
                            value={form.name}
                            onChange={handleFormChange}
                            required
                        />
                        {renderEditCategoryInputs()}
                        <div className="flex items-center space-x-3">
                            <Switch
                                checked={form.is_active}
                                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
                            />
                            <Label>{form.is_active ? 'Active' : 'Inactive'}</Label>
                        </div>
                        {error && <div className="text-danger">{error}</div>}
                        <Button type="submit" loading={saving} loadingText="Saving...">Save</Button>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <ConfirmDialog
                open={!!deleteId}
                onOpenChange={(open) => !open && setDeleteId(null)}
                title="Delete Master Category"
                description="Are you sure you want to delete this master category? This will also delete all associated user categories across all domains. This action cannot be undone."
                confirmLabel="Delete"
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default MasterCategoryManager;
